// ============================================================
// SideQuests - Install Prompt (Track C)
// Shows "Add to Home Screen" after 2+ visits, but ONLY when the
// browser actually offers an install (beforeinstallprompt fires =
// Chrome install heuristics satisfied: SW + manifest + engagement).
// Respects heuristics: never shows on iOS standalone PWA, never
// re-asks after dismissal (localStorage), and iOS Safari falls
// back to a hint banner only after 2 visits + already-engaged.
// ============================================================

import React, { useEffect, useState } from 'react';

const VISIT_KEY = 'sq.visitCount.v1';
const DISMISS_KEY = 'sq.installDismissed.v1';

function bumpVisitCount(): number {
  try {
    const raw = localStorage.getItem(VISIT_KEY);
    const n = (Number(raw) || 0) + 1;
    localStorage.setItem(VISIT_KEY, String(n));
    return n;
  } catch {
    return 1;
  }
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    const onBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      const visits = bumpVisitCount();
      try {
        if (localStorage.getItem(DISMISS_KEY)) return;
      } catch { /* non-fatal */ }
      if (visits >= 2) setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);

    if (!('onbeforeinstallprompt' in window)) {
      const visits = bumpVisitCount();
      try {
        if (localStorage.getItem(DISMISS_KEY)) return;
      } catch { /* non-fatal */ }
      if (visits >= 2) setVisible(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === 'accepted') {
        setVisible(false);
        try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* non-fatal */ }
      }
    } else {
      setVisible(false);
      try {
        localStorage.setItem(DISMISS_KEY, '1');
        sessionStorage.setItem('sq.iosInstallHint', '1');
      } catch { /* non-fatal */ }
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* non-fatal */ }
  };

  if (!visible) return null;

  const isIOS = !deferredPrompt && /iPad|iPhone|iPod/.test(navigator.userAgent || '');

  return (
    <div className="install-prompt" role="dialog" aria-label="Install SideQuests on your device">
      {/* Decorative: the install prompt has its own text label. */}
      <img src="/icons/icon-192.png" alt="" aria-hidden="true" className="install-prompt-icon" />
      <div className="install-prompt-copy">
        <div className="install-prompt-title">Install SideQuests</div>
        <div className="install-prompt-sub">
          {isIOS
            ? 'Tap the share icon and choose "Add to Home Screen" for full-screen app.'
            : deferredPrompt
              ? 'Get full-screen mode, offline quests and instant launches.'
              : 'Add SideQuests to your home screen for a full-screen app experience.'}
        </div>
      </div>
      <div className="install-prompt-actions">
        <button className="install-prompt-btn" onClick={handleDismiss}>Later</button>
        <button className="install-prompt-btn primary" onClick={handleInstall}>
          {isIOS ? 'How to install' : 'Install'}
        </button>
      </div>
    </div>
  );
}
